import { useState, useRef, useEffect } from 'react';
import { Theme } from '@/styles/Theme';
import styled from '@emotion/styled';

interface DateRangePickerProps {
  startValue?: string; // Format: YYYY-MM-DD
  endValue?: string; // Format: YYYY-MM-DD
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  startPlaceholder?: string;
  endPlaceholder?: string;
}

const DateRangePicker = ({
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  startPlaceholder = '연도-월-일',
  endPlaceholder = '연도-월-일',
}: DateRangePickerProps) => {
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const startContainerRef = useRef<HTMLDivElement>(null);
  const endContainerRef = useRef<HTMLDivElement>(null);

  // Parse date string to Date object (local timezone)
  const parseDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
    return new Date(year, month - 1, day); // Local time, no timezone shift
  };

  // Format Date to display string
  const formatDisplay = (date: Date | null): string => {
    if (!date) return '';
    return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
  };

  const [startDate, setStartDate] = useState<Date | null>(() => parseDate(startValue || ''));
  const [endDate, setEndDate] = useState<Date | null>(() => parseDate(endValue || ''));
  const [currentMonth, setCurrentMonth] = useState(() => startDate || endDate || new Date());

  // Sync with props
  useEffect(() => {
    setStartDate(parseDate(startValue || ''));
    if (startValue) {
      setCurrentMonth(parseDate(startValue) || new Date());
    }
  }, [startValue]);

  useEffect(() => {
    setEndDate(parseDate(endValue || ''));
    if (endValue && !startValue) {
      setCurrentMonth(parseDate(endValue) || new Date());
    }
  }, [endValue, startValue]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (startContainerRef.current && !startContainerRef.current.contains(event.target as Node)) {
        setStartOpen(false);
      }
      if (endContainerRef.current && !endContainerRef.current.contains(event.target as Node)) {
        setEndOpen(false);
      }
    };

    if (startOpen || endOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [startOpen, endOpen]);

  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();

  const days: (number | null)[] = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }

  const handleDateClick = (day: number, isStart: boolean) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dateValue = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    if (isStart) {
      setStartDate(newDate);
      onStartChange(dateValue);
      setStartOpen(false);
    } else {
      setEndDate(newDate);
      onEndChange(dateValue);
      setEndOpen(false);
    }
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleClear = (isStart: boolean) => {
    if (isStart) {
      setStartDate(null);
      onStartChange('');
      setStartOpen(false);
    } else {
      setEndDate(null);
      onEndChange('');
      setEndOpen(false);
    }
  };

  const handleToday = (isStart: boolean) => {
    const today = new Date();
    setCurrentMonth(today);
    const dateValue = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    if (isStart) {
      setStartDate(today);
      onStartChange(dateValue);
      setStartOpen(false);
    } else {
      setEndDate(today);
      onEndChange(dateValue);
      setEndOpen(false);
    }
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      currentMonth.getFullYear() === today.getFullYear() &&
      currentMonth.getMonth() === today.getMonth() &&
      day === today.getDate()
    );
  };

  const isSelected = (day: number, isStart: boolean) => {
    const selectedDate = isStart ? startDate : endDate;
    if (!selectedDate) return false;
    return (
      currentMonth.getFullYear() === selectedDate.getFullYear() &&
      currentMonth.getMonth() === selectedDate.getMonth() &&
      day === selectedDate.getDate()
    );
  };

  const renderCalendar = (isStart: boolean) => {
    const isOpen = isStart ? startOpen : endOpen;

    if (!isOpen) return null;

    return (
      <Dropdown>
        <CalendarHeader>
          <NavButton onClick={handlePreviousMonth}>‹</NavButton>
          <MonthYear>
            {currentMonth.getFullYear()}년 {currentMonth.getMonth() + 1}월
          </MonthYear>
          <NavButton onClick={handleNextMonth}>›</NavButton>
        </CalendarHeader>

        <DaysOfWeek>
          {['일', '월', '화', '수', '목', '금', '토'].map((day) => (
            <DayHeader key={day}>{day}</DayHeader>
          ))}
        </DaysOfWeek>

        <DaysGrid>
          {days.map((day, index) => (
            <DayCell
              key={index}
              isEmpty={day === null}
              isToday={day !== null && isToday(day)}
              isSelected={day !== null && isSelected(day, isStart)}
              onClick={() => day !== null && handleDateClick(day, isStart)}
            >
              {day}
            </DayCell>
          ))}
        </DaysGrid>

        <CalendarFooter>
          <FooterButton onClick={() => handleClear(isStart)}>지우기</FooterButton>
          <FooterButton onClick={() => handleToday(isStart)}>오늘</FooterButton>
        </CalendarFooter>
      </Dropdown>
    );
  };

  return (
    <Container>
      <DateInputGroup>
        <InputWrapper ref={startContainerRef}>
          <Input
            type="text"
            readOnly
            value={formatDisplay(startDate)}
            placeholder={startPlaceholder}
            onClick={() => {
              setStartOpen(!startOpen);
              setEndOpen(false);
              if (startDate) {
                setCurrentMonth(startDate);
              } else if (endDate) {
                setCurrentMonth(endDate);
              } else {
                setCurrentMonth(new Date());
              }
            }}
          />
          <CalendarIcon>📅</CalendarIcon>
          {renderCalendar(true)}
        </InputWrapper>

        <Separator>~</Separator>

        <InputWrapper ref={endContainerRef}>
          <Input
            type="text"
            readOnly
            value={formatDisplay(endDate)}
            placeholder={endPlaceholder}
            onClick={() => {
              setEndOpen(!endOpen);
              setStartOpen(false);
              if (endDate) {
                setCurrentMonth(endDate);
              } else if (startDate) {
                setCurrentMonth(startDate);
              } else {
                setCurrentMonth(new Date());
              }
            }}
          />
          <CalendarIcon>📅</CalendarIcon>
          {renderCalendar(false)}
        </InputWrapper>
      </DateInputGroup>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
`;

const DateInputGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 14rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.625rem 2.5rem 0.625rem 0.75rem;
  border: 0.0625rem solid ${Theme.colors.G_3};
  border-radius: 0.25rem;
  font-size: 0.875rem;
  cursor: pointer;
  background-color: white;
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:focus {
    outline: none;
    border-color: ${Theme.colors.B_1};
  }
`;

const CalendarIcon = styled.span`
  position: absolute;
  right: 0.75rem;
  pointer-events: none;
  font-size: 1rem;
`;

const Separator = styled.span`
  font-size: 0.875rem;
  color: ${Theme.colors.BLACK};
  white-space: nowrap;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  z-index: 1000;
  background: white;
  border: 0.0625rem solid ${Theme.colors.G_3};
  border-radius: 0.5rem;
  box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.1);
  padding: 1rem;
  min-width: 16rem;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const NavButton = styled.button`
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: ${Theme.colors.BLACK};
  padding: 0.25rem 0.5rem;

  &:hover {
    background-color: ${Theme.colors.G_4};
    border-radius: 0.25rem;
  }
`;

const MonthYear = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${Theme.colors.BLACK};
`;

const DaysOfWeek = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.25rem;
  margin-bottom: 0.5rem;
`;

const DayHeader = styled.div`
  text-align: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${Theme.colors.BLACK};
  padding: 0.5rem;
`;

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.25rem;
  margin-bottom: 1rem;
`;

const DayCell = styled.button<{ isEmpty: boolean; isToday: boolean; isSelected: boolean }>`
  aspect-ratio: 1;
  border: none;
  background: ${(props) => {
    if (props.isEmpty) return 'transparent';
    if (props.isSelected) return Theme.colors.B_1;
    if (props.isToday) return Theme.colors.G_4;
    return 'white';
  }};
  color: ${(props) => {
    if (props.isEmpty) return 'transparent';
    if (props.isSelected) return 'white';
    return Theme.colors.BLACK;
  }};
  font-size: 0.875rem;
  cursor: ${(props) => (props.isEmpty ? 'default' : 'pointer')};
  border-radius: 0.25rem;

  &:hover {
    background: ${(props) => {
      if (props.isEmpty) return 'transparent';
      if (props.isSelected) return Theme.colors.B_1;
      return Theme.colors.G_4;
    }};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const CalendarFooter = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
`;

const FooterButton = styled.button`
  flex: 1;
  padding: 0.5rem;
  border: 0.0625rem solid ${Theme.colors.G_3};
  border-radius: 0.25rem;
  background: white;
  color: ${Theme.colors.BLACK};
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    background-color: ${Theme.colors.G_4};
  }
`;

export default DateRangePicker;

